import { briefingPlanProposal } from "../lib/briefingPlan";
import { createParkedConversationBudget, loadParkedConversationContext, parkedConversationSessionPath } from "../lib/parkedConversation.mjs";

// A parked, live Karen conversation. The microphone is streamed for the
// conversation only; this path does not create, store, or upload an audio file.
export async function captureKarenBriefing({ signal, ownerId, lessonId, pupilId,
  onRecording, onStopReady, onVoiceUnavailable, onPlanProposed, onEndRequested, onContext, onBudget }) {
  if (typeof RTCPeerConnection === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    throw new Error("Live briefing is unavailable in this browser.");
  }

  const network = new AbortController();
  let microphone, peer, channel, audio, timer, context, budget;
  let closed = false, voiceFailed = false, settled = false;
  let sessionUpdateSent = false, sessionReady = false;
  const handledCalls = new Set();
  let pendingEndAction = null;
  let resolveConversation, rejectConversation;
  const conversation = new Promise((resolve, reject) => {
    resolveConversation = resolve;
    rejectConversation = reject;
  });
  // Cancellation can arrive while the browser's microphone prompt is still
  // pending. Observe the rejection now; awaiting below still reports it.
  void conversation.catch(() => {});
  const stopVoice = () => {
    network.abort();
    clearTimeout(timer);
    budget?.stop();
    if (audio) { audio.pause(); audio.srcObject = null; }
    if (channel) { channel.onopen = null; channel.onmessage = null; channel.onclose = null; channel.onerror = null; channel.close(); }
    if (peer) { peer.ontrack = null; peer.onconnectionstatechange = null; peer.close(); }
  };
  const finish = action => {
    if (settled) return;
    settled = true;
    closed = true;
    stopVoice();
    resolveConversation({ action });
  };
  const cancel = () => {
    closed = true;
    stopVoice();
    if (!settled) {
      settled = true;
      rejectConversation(new Error("Briefing cancelled"));
    }
  };
  const failVoice = (stage = "connection") => {
    if (closed || voiceFailed) return;
    voiceFailed = true;
    stopVoice();
    const message = stage === "playback"
      ? "Karen connected, but her voice could not play. Retry while parked, or continue to the lesson."
      : stage === "provider" && !sessionReady
        ? "Karen's voice session could not be prepared. Retry while parked, or continue to the lesson."
        : "Karen's live briefing could not connect or was interrupted. Retry while parked, or continue to the lesson.";
    onVoiceUnavailable?.(message);
    if (!settled) {
      settled = true;
      rejectConversation(new Error(message));
    }
  };
  signal.addEventListener("abort", cancel, { once: true });

  try {
    if (signal.aborted) throw new Error("Briefing cancelled");
    // Request mic permission directly from the instructor's Briefing tap.
    microphone = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    if (signal.aborted) throw new Error("Briefing cancelled");
    // Do not stream the shared microphone before the lesson context is accepted.
    microphone.getAudioTracks().forEach(track => { track.enabled = false; });

    timer = setTimeout(failVoice, 30000);
    context = await loadParkedConversationContext({ lessonId, mode: "briefing", signal: network.signal, fetcher: fetch });
    if (signal.aborted || closed || voiceFailed) throw new Error("Briefing cancelled");
    if (String(context.student_id || "") !== String(pupilId || "")) throw new Error("The saved plan does not match this pupil. Retry while parked.");
    onContext?.(context);

    peer = new RTCPeerConnection();
    audio = new Audio();
    audio.autoplay = true;
    for (const track of microphone.getAudioTracks()) peer.addTrack(track, microphone);
    peer.ontrack = event => {
      if (closed || voiceFailed) return;
      audio.srcObject = event.streams[0] || new MediaStream([event.track]);
      audio.play().catch(() => failVoice("playback"));
    };
    peer.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(peer.connectionState)) failVoice();
    };
    channel = peer.createDataChannel("oai-events");

    const prepareSession = () => {
      if (closed || voiceFailed || channel.readyState !== "open") return;
      sessionUpdateSent = true;
      // The authenticated SDP endpoint supplies the full owned plan and
      // policy. Do not replace it with a partial client-side prompt.
      channel.send(JSON.stringify({ type: "session.update", session: { type: "realtime" } }));
    };

    channel.onopen = prepareSession;
    channel.onerror = () => failVoice("channel");
    channel.onclose = () => failVoice("channel");
    channel.onmessage = event => {
      if (closed || voiceFailed || signal.aborted) return;
      let value;
      try { value = JSON.parse(event.data); } catch { failVoice(); return; }
      if (value.type === "session.updated" && sessionUpdateSent && !sessionReady) {
        sessionReady = true;
        clearTimeout(timer);
        budget = createParkedConversationBudget({ ownerId, lessonId, mode: "briefing", onUpdate: onBudget,
          onPacing: pacing => {
            if (closed || voiceFailed || signal.aborted || channel.readyState !== "open") return false;
            channel.send(JSON.stringify(pacing));
            return true;
          },
        });
        budget.start();
        microphone.getAudioTracks().forEach(track => { track.enabled = true; });
        onRecording?.();
        channel.send(JSON.stringify({ type: "response.create", response: {
          output_modalities: ["audio"], tool_choice: "none",
        } }));
        return;
      }
      if (value.type !== "response.done" || value.response?.status !== "completed") {
        if (value.type === "error" || (value.type === "response.done" && value.response?.status === "failed")) failVoice("provider");
        return;
      }

      let replied = false;
      let endingNow = false;
      for (const item of value.response.output || []) {
        if (item.type !== "function_call" || !item.call_id || handledCalls.has(item.call_id)) continue;
        handledCalls.add(item.call_id);
        let output;
        try {
          if (item.name === "end_parked_briefing") {
            const args = JSON.parse(item.arguments || "{}");
            if (!["stop_here", "continue_lesson"].includes(args.action)) {
              throw new Error("Please confirm whether to stop listening or continue to the lesson.");
            }
            pendingEndAction = args.action;
            endingNow = true;
            output = { status: "ending", action: args.action, saved: false };
          } else if (item.name === "propose_briefing_plan" && onPlanProposed) {
            const proposal = briefingPlanProposal(JSON.parse(item.arguments));
            onPlanProposed(proposal);
            output = { status: "pending_end", saved: false, ...proposal,
              message: "Displayed on screen. The instructor saves the agreed choices when ending the conversation." };
          } else {
            throw new Error("This briefing action is unavailable.");
          }
        } catch (failure) {
          output = { status: "not_saved", saved: false, message: failure.message };
        }
        channel.send(JSON.stringify({ type: "conversation.item.create", item: {
          type: "function_call_output", call_id: item.call_id, output: JSON.stringify(output),
        } }));
        replied = true;
      }
      if (replied) {
        channel.send(JSON.stringify(endingNow
          ? { type: "response.create", response: {
              output_modalities: ["audio"], tool_choice: "none",
              instructions: "Say a brief, warm sign-off. If the instructor chose continue_lesson, say you are moving to the lesson. Otherwise say you are stopping listening now.",
            } }
          : { type: "response.create" }));
      } else if (pendingEndAction) {
        const action = pendingEndAction;
        pendingEndAction = null;
        setTimeout(() => {
          if (!closed && !signal.aborted) onEndRequested?.(action);
        }, 650);
      }
    };

    const connect = async () => {
      try {
        const offer = await peer.createOffer();
        if (closed || voiceFailed) return;
        await peer.setLocalDescription(offer);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ""}${parkedConversationSessionPath(context)}`,
          {
            method: "POST", credentials: "include", signal: network.signal,
            headers: { "Content-Type": "application/sdp" }, body: offer.sdp,
          },
        );
        if (!response.ok) throw new Error("Briefing connection unavailable");
        const sdp = await response.text();
        if (!closed && !voiceFailed) await peer.setRemoteDescription({ type: "answer", sdp });
      } catch { failVoice(); }
    };

    onStopReady?.(finish);
    void connect();
    return await conversation;
  } finally {
    closed = true;
    stopVoice();
    signal.removeEventListener("abort", cancel);
    microphone?.getTracks().forEach(track => track.stop());
  }
}

